import { InputWrapper } from './styled';
import { useField } from 'formik';
import React, { useRef, useState } from 'react';
import InputPhone, { PhoneInputProps as InputPhoneProps } from 'react-phone-number-input';
import {
  ErrorLabel,
  HintLabel,
  Label,
  LinkLabel,
  Input
} from '@service-victoria/component-library/components/Input/styled';

// populate the flags attribute only for dev, in prod we lazy-load the flags in from the public url
//   See postbuild step in package.json for details on how that is created
const { default: flags } =
  process.env.NODE_ENV !== 'production'
    ? require('react-phone-number-input/flags')
    : { default: undefined };

export type PhoneInputProps = Partial<InputPhoneProps> & {
  /**
   * Mandatory input field unique name
   */
  name: string;
  /**
   * Optional Label to show at top of the input box. Default ''
   */
  label?: string;
  /**
   * Optional overriding accessibility label for screen-reader users. Falls back to 'label' prop.
   */
  ariaLabel?: string;
  /**
   * Optional Hint message to explain what was the error from. default 'undefined'
   */
  hint?: string;
  /**
   * Optional Link to refer the user to. Default 'undefined'
   */
  linkText?: string;
  /**
   * Input field error message
   */
  error?: string;
};

const flagUrl = `${process.env.PUBLIC_URL}/static/media/flags/{XX}.svg`;

export const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  label = '',
  ariaLabel = '',
  type = 'text',
  linkText,
  hint,
  children,
  onChange,
  error,
  ...props
}) => {
  const [{ value, onBlur }, meta, { setValue, setTouched }] = useField<string>(name);
  const [countryLn, setCountryLn] = useState(3); // 3 for +61 for AU
  const ref = useRef<HTMLInputElement>(null);

  /**
   * Displaying a user error message
   */
  const showError = meta.touched && meta.error;

  return (
    <InputWrapper>
      <Label htmlFor={name} data-testid='PhoneInputLabel' id={`${name}.label`}>
        {label}
      </Label>
      <InputPhone
        countryOptionsOrder={['AU', 'NZ', '|']}
        defaultCountry='AU'
        international
        countryCallingCodeEditable={false}
        onCountryChange={() => {
          if (ref.current) {
            setCountryLn(ref.current.value.length);
          }
          setTouched(false);
        }}
        onFocus={setCaretToEnd(countryLn)}
        onClick={setCaretToEnd(countryLn)}
        name={name}
        ref={ref}
        onBlur={onBlur}
        type='tel'
        aria-label={ariaLabel || label}
        aria-invalid={!!showError}
        aria-labelledby={`${name}.label`}
        countrySelectProps={{
          'data-testid': 'PhoneCountry-Select'
        }}
        numberInputProps={{
          'data-testid': 'PhoneNumber-Input'
        }}
        inputComponent={Input}
        onChange={setValue}
        value={value}
        flagUrl={flagUrl}
        flags={flags}
        {...props}
      />
      {children}
      {!!showError && <ErrorLabel data-testid='PhoneInputError'>{meta.error}</ErrorLabel>}
      {hint && <HintLabel data-testid='PhoneInputError'>{hint}</HintLabel>}
      {linkText && (
        <LinkLabel as='button' data-testid='PhoneInputLinkLabel'>
          {linkText}
        </LinkLabel>
      )}
    </InputWrapper>
  );
};

function setCaretToEnd(maxLength: number) {
  return e => {
    const target = e.target as HTMLInputElement;
    if (target.value.length <= maxLength) {
      target.selectionStart = target.selectionEnd = target.value.length;

      // chrome is janky...
      setTimeout(() => {
        target.selectionStart = target.selectionEnd = target.value.length;
      }, 1);
    }
  };
}

export default PhoneInput;
